import { Building2, CreditCard, Rocket, Smartphone, Users } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import DataQualityBadge from '../../components/analytics/DataQualityBadge';
import MetricDefinitionsDrawer from '../../components/analytics/MetricDefinitionsDrawer';
import PageGuide from '../../components/analytics/PageGuide';
import { analyticsService } from '../../services/api';
import '../Analytics.css';
import AnalyticsLayout from './AnalyticsLayout';

function getDefaultFromDate() {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return date.toISOString().split('T')[0];
}

const formatNumber = (value) =>
    new Intl.NumberFormat('it-IT').format(value || 0);

function FunnelBar({ label, count, total, color }) {
    const pct = total > 0 ? Math.round((count / total) * 100) : 0;
    return (
        <div style={{ marginBottom: '10px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', fontSize: '0.8rem' }}>
                <span style={{ color: '#475569' }}>{label}</span>
                <span style={{ fontWeight: 600, color: '#1e293b' }}>{formatNumber(count)} <span style={{ color: '#94a3b8', fontWeight: 400 }}>({pct}%)</span></span>
            </div>
            <div style={{ height: '8px', background: '#e2e8f0', borderRadius: '4px', overflow: 'hidden' }}>
                <div style={{ height: '100%', width: `${pct}%`, background: color || '#6366f1', borderRadius: '4px', transition: 'width 0.4s' }} />
            </div>
        </div>
    );
}

export default function AnalyticsActivation() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filters, setFilters] = useState({
        from: getDefaultFromDate(),
        to: new Date().toISOString().split('T')[0]
    });

    const loadData = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const result = await analyticsService.getActivation({ from: filters.from, to: filters.to });
            setData(result);
        } catch (err) {
            console.error('Failed to load activation data:', err);
            setError(err.response?.data?.message || err.message || 'Errore nel caricamento dei dati');
        } finally {
            setLoading(false);
        }
    }, [filters]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    return (
        <AnalyticsLayout filters={filters} onFiltersChange={setFilters}>
            <PageGuide
                title="Funnel clienti e percettori"
                summary="Stato di attivazione di tenant, device, locali e percettori. Mostra i punti di blocco nel funnel di onboarding e lo stato Stripe Connect dei percettori."
            />
            {loading && !data && (
                <div className="analytics-loading">
                    <div className="spinner"></div>
                    <p>Caricamento dati activation...</p>
                </div>
            )}

            {error && (
                <div className="alert alert-error" style={{ marginBottom: '20px' }}>
                    <strong>Errore:</strong> {error}
                </div>
            )}

            {data && (
                <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                        <span style={{ fontSize: '0.8rem', color: '#64748b' }}>
                            Periodo: {data.period?.from} → {data.period?.to} · I contatori "totali" sono cumulativi
                        </span>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                            <MetricDefinitionsDrawer metricKeys={['activeWorkers', 'workerKycCompletionRate', 'activeVenues', 'venueActivationRate', 'activeDevices']} />
                            <button className="btn-refresh" onClick={loadData} disabled={loading} style={{ fontSize: '0.8rem', padding: '6px 12px' }}>
                                {loading ? '⏳' : '🔄'} Aggiorna
                            </button>
                        </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>

                        {/* Tenant Funnel */}
                        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '20px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                                <Building2 size={18} color="#6366f1" />
                                <h3 style={{ margin: 0, fontSize: '0.9rem', fontWeight: 600, color: '#1e293b' }}>Tenant Funnel</h3>
                            </div>
                            <FunnelBar label="Registrati (totale)" count={data.tenants?.total} total={data.tenants?.total} color="#6366f1" />
                            <FunnelBar label="Attivi" count={data.tenants?.active} total={data.tenants?.total} color="#10b981" />
                            <FunnelBar label="Onboarding / Pending" count={data.tenants?.onboardingPending} total={data.tenants?.total} color="#0ea5e9" />
                            <FunnelBar label="Sospesi" count={data.tenants?.suspended} total={data.tenants?.total} color="#f59e0b" />
                            <FunnelBar label="Chiusi" count={data.tenants?.closed} total={data.tenants?.total} color="#ef4444" />
                            {data.tenants?.isBreakdownReconciled === false && (
                                <div style={{ marginTop: '10px', fontSize: '0.78rem', color: '#dc2626' }}>
                                    ⚠ Breakdown tenant non riconciliato: {data.tenants?.statusBreakdownTotal || 0} / {data.tenants?.total || 0}
                                </div>
                            )}
                            <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem' }}>
                                <span style={{ color: '#64748b' }}>Nuovi nel periodo</span>
                                <span style={{ fontWeight: 600, color: '#6366f1' }}>+{data.tenants?.newInPeriod || 0}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', marginTop: '4px' }}>
                                <span style={{ color: '#64748b' }}>Activation rate</span>
                                <span style={{ fontWeight: 600, color: '#10b981' }}>{data.tenants?.activationRate || 0}%</span>
                            </div>
                        </div>

                        {/* Device Funnel */}
                        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '20px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                                <Smartphone size={18} color="#0ea5e9" />
                                <h3 style={{ margin: 0, fontSize: '0.9rem', fontWeight: 600, color: '#1e293b' }}>Device Funnel</h3>
                            </div>
                            <FunnelBar label="Totale device" count={data.devices?.total} total={data.devices?.total} color="#0ea5e9" />
                            <FunnelBar label="Attivi" count={data.devices?.active} total={data.devices?.total} color="#10b981" />
                            <FunnelBar label="Provisioned" count={data.devices?.provisioned} total={data.devices?.total} color="#6366f1" />
                            <FunnelBar label="Spediti (in attesa attivazione)" count={data.devices?.shippedPendingActivation} total={data.devices?.total} color="#f59e0b" />
                            <FunnelBar label="Disabilitati" count={data.devices?.disabled} total={data.devices?.total} color="#ef4444" />
                            <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem' }}>
                                <span style={{ color: '#64748b' }}>Activation rate</span>
                                <span style={{ fontWeight: 600, color: '#10b981' }}>{data.devices?.activationRate || 0}%</span>
                            </div>
                        </div>

                        {/* Worker Invite Funnel */}
                        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '20px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                                <Users size={18} color="#f59e0b" />
                                <h3 style={{ margin: 0, fontSize: '0.9rem', fontWeight: 600, color: '#1e293b' }}>Inviti Percettori (periodo)</h3>
                            </div>
                            <FunnelBar label="Inviti inviati" count={data.workerInvites?.inPeriod?.invited} total={data.workerInvites?.inPeriod?.invited} color="#6366f1" />
                            <FunnelBar label="Accettati" count={data.workerInvites?.inPeriod?.accepted} total={data.workerInvites?.inPeriod?.invited} color="#10b981" />
                            <FunnelBar label="Rifiutati" count={data.workerInvites?.inPeriod?.declined} total={data.workerInvites?.inPeriod?.invited} color="#ef4444" />
                            <FunnelBar label="In attesa" count={data.workerInvites?.inPeriod?.pending} total={data.workerInvites?.inPeriod?.invited} color="#f59e0b" />
                            <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem' }}>
                                <span style={{ color: '#64748b' }}>Acceptance rate (periodo)</span>
                                <span style={{ fontWeight: 600, color: '#10b981' }}>{data.workerInvites?.inPeriod?.acceptanceRate || 0}%</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', marginTop: '4px' }}>
                                <span style={{ color: '#64748b' }}>Acceptance rate (all-time)</span>
                                <span style={{ fontWeight: 600, color: '#10b981' }}>{data.workerInvites?.allTime?.acceptanceRate || 0}%</span>
                            </div>
                        </div>

                        {/* Stripe Connect Funnel */}
                        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '20px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                                <CreditCard size={18} color="#8b5cf6" />
                                <h3 style={{ margin: 0, fontSize: '0.9rem', fontWeight: 600, color: '#1e293b' }}>Stripe Connect (all-time)</h3>
                                <DataQualityBadge status="partial" note="Dato all-time, non filtrabile per periodo" />
                            </div>
                            <FunnelBar label="Onboarding avviato" count={data.stripeConnect?.started} total={data.stripeConnect?.started} color="#8b5cf6" />
                            <FunnelBar label="Completato (can_receive)" count={data.stripeConnect?.completed} total={data.stripeConnect?.started} color="#10b981" />
                            <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem' }}>
                                <span style={{ color: '#64748b' }}>Completion rate</span>
                                <span style={{ fontWeight: 600, color: '#10b981' }}>{data.stripeConnect?.completionRate || 0}%</span>
                            </div>
                        </div>

                        {/* Venues */}
                        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '20px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                                <Rocket size={18} color="#10b981" />
                                <h3 style={{ margin: 0, fontSize: '0.9rem', fontWeight: 600, color: '#1e293b' }}>Locali</h3>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}>
                                <span style={{ fontSize: '0.85rem', color: '#475569' }}>Totale locali</span>
                                <span style={{ fontWeight: 700, fontSize: '1.1rem', color: '#1e293b' }}>{formatNumber(data.venues?.total)}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
                                <span style={{ fontSize: '0.85rem', color: '#475569' }}>Nuovi nel periodo</span>
                                <span style={{ fontWeight: 600, color: '#10b981' }}>+{data.venues?.newInPeriod || 0}</span>
                            </div>
                        </div>

                        {/* Worker totals */}
                        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '20px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                                <Users size={18} color="#64748b" />
                                <h3 style={{ margin: 0, fontSize: '0.9rem', fontWeight: 600, color: '#1e293b' }}>Percettori (all-time)</h3>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f1f5f9' }}>
                                <span style={{ fontSize: '0.85rem', color: '#475569' }}>Inviti totali</span>
                                <span style={{ fontWeight: 700, fontSize: '1.1rem', color: '#1e293b' }}>{formatNumber(data.workerInvites?.allTime?.invited)}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
                                <span style={{ fontSize: '0.85rem', color: '#475569' }}>Accettati</span>
                                <span style={{ fontWeight: 600, color: '#10b981' }}>{formatNumber(data.workerInvites?.allTime?.accepted)}</span>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </AnalyticsLayout>
    );
}
